import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { FlowCenterModal } from '@/components/FlowCenter/FlowCenterModal';
import FileViewerModal from '@/components/FileViewerModal';
import { useRequest } from 'ahooks';
import { getBizDataDetail, startFlow, approveFlow } from '@/services';
import { useFormEvent } from '@/store/flowExcelStore';

/** 文件预览状态：与 ModalState 互相独立，避免相互 reset 时被串 */

const ModalContext = /*#__PURE__*/createContext({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openFormModal: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeFormModal: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openExcelModal: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeExcelModal: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  open: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  close: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openFileViewer: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeFileViewer: () => {},
  refreshKey: 0
});
const INITIAL_FILE_VIEWER = {
  visible: false,
  props: {
    filename: ''
  }
};
export function useModal() {
  return useContext(ModalContext);
}
const DEFAULT_CONFIG = {
  width: 1200,
  top: 100
};
const INITIAL_MODAL = {
  visible: false
};
export function ModalProvider({
  children
}) {
  const [formEvent] = useFormEvent();
  const [refreshKey, setRefreshKey] = useState(0);
  // 合并 formConfig / excelConfig 为单一 activeConfig —— FlowCenterModal 内部
  // 按 formCode 前缀区分走 Form / Excel 分支，不需要两份独立 state。
  const [activeConfig, setActiveConfig] = useState({
    visible: false
  });
  const [fileViewer, setFileViewer] = useState(INITIAL_FILE_VIEWER);
  const startFlowApi = useRequest(startFlow, {
    manual: true
  });
  const approveFlowApi = useRequest(approveFlow, {
    manual: true,
    onSuccess: () => {
      close();
      setRefreshKey(k => k + 1);
      _Message.success('审批完成');
    },
    onError: () => {
      _Message.error('审批失败');
    }
  });
  const getBizDataDetailApi = useRequest(getBizDataDetail, {
    manual: true,
    onSuccess: res => {
      setActiveConfig(prev => ({
        ...prev,
        formData: {
          ...prev.formData,
          ...res
        }
      }));
    }
  });
  useEffect(() => {
    if (!activeConfig.bizId || activeConfig.visible !== true) return;
    getBizDataDetailApi.runAsync(activeConfig.bizId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConfig.bizId, activeConfig.visible]);
  const openFormModal = useCallback(c => setActiveConfig({
    ...INITIAL_MODAL,
    ...DEFAULT_CONFIG,
    visible: true,
    ...c
  }), []);
  const closeFormModal = useCallback(() => setActiveConfig(INITIAL_MODAL), []);
  const openExcelModal = useCallback(c => setActiveConfig({
    ...INITIAL_MODAL,
    ...DEFAULT_CONFIG,
    width: 1300,
    visible: true,
    ...c
  }), []);
  const closeExcelModal = useCallback(() => setActiveConfig(INITIAL_MODAL), []);
  const openFileViewer = useCallback(props => setFileViewer({
    visible: true,
    props
  }), []);
  const closeFileViewer = useCallback(() => setFileViewer(INITIAL_FILE_VIEWER), []);

  /**
   * 通用 open：按 formCode 前缀分发。
   *   - 'excel_' → openExcelModal
   *   - 其他 → openFormModal
   */
  const open = useCallback(c => {
    const code = c?.formCode ?? '';
    if (code.startsWith('excel_')) {
      openExcelModal(c);
    } else {
      openFormModal(c);
    }
  }, [openExcelModal, openFormModal]);

  /** 通用 close：单一 activeConfig 直接复位即可。 */
  const close = useCallback(() => setActiveConfig(INITIAL_MODAL), []);

  /**
   * 通用提交（新增/暂存）：原来 Form / Excel 各一份实现，现在 modal 已合并
   * 成 FlowCenterModal 一份 activeConfig —— close 统一为 reset activeConfig，
   * 暂存提示只在 Form 分支（form_leave 业务）才用得到。Excel 分支的 draft
   * 永远走不到，所以判断一次 formCode 即可。
   */

  const save = async values => {
    try {
      await startFlowApi.runAsync({
        ...values,
        instanceId: activeConfig?.instanceId
      });
      close();
      setRefreshKey(k => k + 1);
      // 暂存提示只有 Form 分支会触发（Excel 分支无暂存按钮），
      // 用 formCode 前缀判断业务类型。
      const isExcel = activeConfig.formCode?.startsWith('excel_');
      if (values.draft === true && !isExcel) {
        _Message.success('已暂存');
      } else {
        _Message.success('流程已发起');
      }
    } catch {
      _Message.error('操作失败');
    }
  };
  const handleStartFlow = async values => {
    if (formEvent?.onSubmitBefore) {
      formEvent.onSubmitBefore(values, payload => {
        save(payload);
      });
      return;
    }
    save(values);
  };

  /**
   * 通用审批提交：原 onApproveSubmit / onExcelApproveSubmit 实现相同，
   * 合并为一个（instanceId 从 activeConfig 取）。
   */
  const handleApproveFlow = async values => {
    await approveFlowApi.runAsync({
      instanceId: activeConfig?.instanceId,
      ...values
    });
  };
  return /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: {
      openFormModal,
      closeFormModal,
      openExcelModal,
      closeExcelModal,
      open,
      close,
      openFileViewer,
      closeFileViewer,
      refreshKey
    }
  }, children, /*#__PURE__*/React.createElement(FlowCenterModal, {
    formCode: activeConfig.formCode,
    visible: activeConfig.visible,
    width: activeConfig.width,
    top: activeConfig.top,
    mode: activeConfig.mode,
    type: activeConfig.type,
    data: activeConfig.formData,
    instanceId: activeConfig.instanceId,
    nodeId: activeConfig.nodeId,
    loading: getBizDataDetailApi.loading,
    onSubmit: activeConfig.onSubmit || handleStartFlow,
    onApproveSubmit: handleApproveFlow,
    onCancel: close
  }), /*#__PURE__*/React.createElement(FileViewerModal, _extends({
    visible: fileViewer.visible
  }, fileViewer.props, {
    onCancel: closeFileViewer
  })));
}